// ./src/i18n/index.ts
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

// ============================================
// English — bundled statically (default/fallback)
// ============================================

import enCommon from "./locales/en/common.json";
import enValidation from "./locales/en/validation.json";
import enTags from "./locales/en/tags.json";
import enSupport from "./locales/en/support.json";
// ⚠ Plan-gate copy: what a locked page tells the host they would get. English only for now —
// i18next falls back to `en` for a namespace a language has not translated, so ru/fr/es keep
// working and simply read the English text until somebody writes theirs.
import enPlans from "./locales/en/plans.json";

// ============================================
// Types
// ============================================

export type SupportedLanguage = "en" | "ru" | "fr" | "es";

export const SUPPORTED_LANGUAGES: SupportedLanguage[] = [
  "en",
  "ru",
  "fr",
  "es",
];

export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  en: "English",
  ru: "Русский",
  fr: "Français",
  es: "Español",
};

export const DEFAULT_LANGUAGE: SupportedLanguage = "en";

const ALL_NAMESPACES = [
  "common",
  "validation",
  "tags",
  "support",
  "plans",
] as const;

export type AppNamespace = (typeof ALL_NAMESPACES)[number];

// ============================================
// Dynamic language loaders (RU/FR/ES)
// ============================================

const languageLoaders: Record<
  Exclude<SupportedLanguage, "en">,
  () => Promise<Record<string, Record<string, unknown>>>
> = {
  ru: async () => {
    const modules = await Promise.all([
      import("./locales/ru/common.json"),
      import("./locales/ru/validation.json"),
      import("./locales/ru/tags.json"),
      import("./locales/ru/support.json"),
    ]);

    const result: Record<string, Record<string, unknown>> = {};
    ALL_NAMESPACES.forEach((ns, i) => {
      result[ns] = modules[i].default;
    });
    return result;
  },
  fr: async () => {
    const modules = await Promise.all([
      import("./locales/fr/common.json"),
      import("./locales/fr/validation.json"),
      import("./locales/fr/tags.json"),
      import("./locales/fr/support.json"),
    ]);
    const result: Record<string, Record<string, unknown>> = {};
    ALL_NAMESPACES.forEach((ns, i) => {
      result[ns] = modules[i].default;
    });
    return result;
  },
  es: async () => {
    const modules = await Promise.all([
      import("./locales/es/common.json"),
      import("./locales/es/validation.json"),
      import("./locales/es/tags.json"),
      import("./locales/es/support.json"),
    ]);
    const result: Record<string, Record<string, unknown>> = {};
    ALL_NAMESPACES.forEach((ns, i) => {
      result[ns] = modules[i].default;
    });
    return result;
  },
};

// Track loaded languages to avoid duplicate loads
const loadedLanguages = new Set<SupportedLanguage>(["en"]);

// ============================================
// Public API
// ============================================

/**
 * Load a language and switch to it.
 * English switches instantly (already bundled).
 * Other languages are loaded dynamically, then activated.
 *
 * @returns true if the language was loaded and activated successfully
 */
export const loadLanguage = async (language: string): Promise<boolean> => {
  if (!isLanguageSupported(language)) {
    return false;
  }

  if (language === "en") {
    await i18n.changeLanguage("en");
    return true;
  }

  if (!loadedLanguages.has(language)) {
    const loader = languageLoaders[language];
    const bundles = await loader();

    for (const ns of ALL_NAMESPACES) {
      i18n.addResourceBundle(language, ns, bundles[ns], true, true);
    }

    loadedLanguages.add(language);
  }

  await i18n.changeLanguage(language);
  return true;
};

// ============================================
// Resources — English only
// ============================================

const resources = {
  en: {
    common: enCommon,
    validation: enValidation,
    tags: enTags,
    support: enSupport,
    plans: enPlans,
  },
};

// ============================================
// Initialize i18n
// ============================================

i18n.use(initReactI18next).init({
  resources,
  lng: DEFAULT_LANGUAGE,
  fallbackLng: DEFAULT_LANGUAGE,
  defaultNS: "common",
  ns: [...ALL_NAMESPACES],

  interpolation: {
    escapeValue: false,
  },

  react: {
    useSuspense: false,
  },
});

// ============================================
// Helper Functions
// ============================================

export const getCurrentLanguage = (): SupportedLanguage => {
  return (i18n.language as SupportedLanguage) || DEFAULT_LANGUAGE;
};

export const isLanguageSupported = (
  language: string
): language is SupportedLanguage => {
  return SUPPORTED_LANGUAGES.includes(language as SupportedLanguage);
};

/**
 * Change the current language.
 * Wraps loadLanguage for backward compatibility.
 * @param language - Language code to switch to
 */
export const changeLanguage = async (language: string): Promise<void> => {
  await loadLanguage(language);
};

export default i18n;
